import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
    PaymentGatewayProviderDto,
    PaymentGatewayConfigDto,
    CurrencyPaymentGatewayDto,
    CreateCurrencyPaymentGatewayRequestDto,
    UpdateCurrencyPaymentGatewayRequestDto,
    PayoutFieldRequirementDto,
    CreatePayoutFieldRequirementRequestDto,
    UpdatePayoutFieldRequirementRequestDto
} from '../_model/payment-admin';
import {
    AdminWithdrawalBeneficiaryDto,
    WithdrawalAdminFilterDto,
    WithdrawalAdminListItemDto,
    WithdrawalAdminDetailDto,
    CompleteWithdrawalRequestDto,
    FailWithdrawalRequestDto
} from '../_model/withdrawal';

export interface ApiResponse<T> {
    success: boolean;
    message: string;
    data: T;
    meta?: any;
}

@Injectable({
    providedIn: 'root'
})
export class WithdrawalAdminService {
    private readonly baseUrl = `${environment.apiBaseUrl}/admin/payment-gateways`;
    private readonly withdrawalUrl = `${environment.apiBaseUrl}/Withdrawals`;
    private readonly adminWithdrawalUrl = `${environment.apiBaseUrl}/AdminWithdrawals`;
    private readonly globalLookupUrl = `${environment.apiBaseUrl}/GlobalLookups`;

    constructor(private http: HttpClient) { }

    getProviders(): Observable<ApiResponse<PaymentGatewayProviderDto[]>> {
        return this.http.get<ApiResponse<PaymentGatewayProviderDto[]>>(
            `${this.baseUrl}/providers`
        );
    }

    getConfigs(): Observable<ApiResponse<PaymentGatewayConfigDto[]>> {
        return this.http.get<ApiResponse<PaymentGatewayConfigDto[]>>(
            `${this.baseUrl}/configs`
        );
    }

    getCurrencyMappings(currencyCode?: string, workflow?: string): Observable<ApiResponse<CurrencyPaymentGatewayDto[]>> {
        let params = new HttpParams();

        if (currencyCode) {
            params = params.set('currencyCode', currencyCode);
        }

        if (workflow) {
            params = params.set('workflow', workflow);
        }

        return this.http.get<ApiResponse<CurrencyPaymentGatewayDto[]>>(
            `${this.baseUrl}/currency-mappings`,
            { params }
        );
    }

    createCurrencyMapping(
        payload: CreateCurrencyPaymentGatewayRequestDto
    ): Observable<ApiResponse<CurrencyPaymentGatewayDto>> {
        return this.http.post<ApiResponse<CurrencyPaymentGatewayDto>>(
            `${this.baseUrl}/currency-mappings`,
            payload
        );
    }

    updateCurrencyMapping(
        id: string,
        payload: UpdateCurrencyPaymentGatewayRequestDto
    ): Observable<ApiResponse<CurrencyPaymentGatewayDto>> {
        return this.http.put<ApiResponse<CurrencyPaymentGatewayDto>>(
            `${this.baseUrl}/currency-mappings/${id}`,
            payload
        );
    }

    setCurrencyMappingDefault(
        id: string
    ): Observable<ApiResponse<CurrencyPaymentGatewayDto>> {
        return this.http.put<ApiResponse<CurrencyPaymentGatewayDto>>(
            `${this.baseUrl}/currency-mappings/${id}/default`,
            {}
        );
    }

    toggleCurrencyMappingStatus(
        id: string
    ): Observable<ApiResponse<CurrencyPaymentGatewayDto>> {
        return this.http.put<ApiResponse<CurrencyPaymentGatewayDto>>(
            `${this.baseUrl}/currency-mappings/${id}/toggle-status`,
            {}
        );
    }

    getPayoutFields(
        jurisdictionCode?: string,
        paymentGatewayConfigId?: string,
        paymentMethod?: string
    ): Observable<ApiResponse<PayoutFieldRequirementDto[]>> {
        let params = new HttpParams();

        if (jurisdictionCode) {
            params = params.set('jurisdictionCode', jurisdictionCode);
        }

        if (paymentGatewayConfigId) {
            params = params.set('paymentGatewayConfigId', paymentGatewayConfigId);
        }

        if (paymentMethod) {
            params = params.set('paymentMethod', paymentMethod);
        }

        return this.http.get<ApiResponse<PayoutFieldRequirementDto[]>>(
            `${this.baseUrl}/payout-fields`,
            { params }
        );
    }

    createPayoutField(
        payload: CreatePayoutFieldRequirementRequestDto
    ): Observable<ApiResponse<PayoutFieldRequirementDto>> {
        return this.http.post<ApiResponse<PayoutFieldRequirementDto>>(
            `${this.baseUrl}/payout-fields`,
            payload
        );
    }

    updatePayoutField(
        id: string,
        payload: UpdatePayoutFieldRequirementRequestDto
    ): Observable<ApiResponse<PayoutFieldRequirementDto>> {
        return this.http.put<ApiResponse<PayoutFieldRequirementDto>>(
            `${this.baseUrl}/payout-fields/${id}`,
            payload
        );
    }

    deletePayoutField(
        id: string
    ): Observable<ApiResponse<any>> {
        return this.http.delete<ApiResponse<any>>(
            `${this.baseUrl}/payout-fields/${id}`
        );
    }

    togglePayoutFieldStatus(
        id: string
    ): Observable<ApiResponse<PayoutFieldRequirementDto>> {
        return this.http.put<ApiResponse<PayoutFieldRequirementDto>>(
            `${this.baseUrl}/payout-fields/${id}/toggle-status`,
            {}
        );
    }

    syncInstitutions(countryCode: string, providerCode: string): Observable<ApiResponse<any>> {
        return this.http.post<ApiResponse<any>>(
            `${this.globalLookupUrl}/sync-institutions`,
            {
                lookupType: 'institution',
                countryCode,
                providerCode
            }
        );
    }

    getWithdrawalRequests(
        filters?: WithdrawalAdminFilterDto
    ): Observable<ApiResponse<WithdrawalAdminListItemDto[]>> {
        let params = new HttpParams();

        if (filters?.status) params = params.set('status', filters.status);
        if (filters?.currencyCode) params = params.set('currencyCode', filters.currencyCode);
        if (filters?.paymentGatewayCode) params = params.set('paymentGatewayCode', filters.paymentGatewayCode);
        if (filters?.paymentMethod) params = params.set('paymentMethod', filters.paymentMethod);
        if (filters?.externalReference) params = params.set('externalReference', filters.externalReference);
        if (filters?.gatewayReference) params = params.set('gatewayReference', filters.gatewayReference);
        if (filters?.userId) params = params.set('userId', filters.userId);
        if (filters?.riskReviewStatus) params = params.set('riskReviewStatus', filters.riskReviewStatus);
        if (filters?.fromDate) params = params.set('fromDate', filters.fromDate);
        if (filters?.toDate) params = params.set('toDate', filters.toDate);
        if (filters?.sortBy) params = params.set('sortBy', filters.sortBy);
        if (filters?.sortDirection) params = params.set('sortDirection', filters.sortDirection);
        if (filters?.page != null) params = params.set('page', filters.page);
        if (filters?.size != null) params = params.set('size', filters.size);

        return this.http.get<ApiResponse<WithdrawalAdminListItemDto[]>>(
            `${this.adminWithdrawalUrl}`,
            { params }
        );
    }

    getWithdrawalRequestById(
        withdrawalId: string
    ): Observable<ApiResponse<WithdrawalAdminDetailDto>> {
        return this.http.get<ApiResponse<WithdrawalAdminDetailDto>>(
            `${this.adminWithdrawalUrl}/${withdrawalId}`
        );
    }

    completeWithdrawalRequest(
        withdrawalId: string,
        payload: CompleteWithdrawalRequestDto
    ): Observable<ApiResponse<WithdrawalAdminDetailDto>> {
        return this.http.post<ApiResponse<WithdrawalAdminDetailDto>>(
            `${this.withdrawalUrl}/${withdrawalId}/complete`,
            payload
        );
    }

    failWithdrawalRequest(
        withdrawalId: string,
        payload: FailWithdrawalRequestDto
    ): Observable<ApiResponse<WithdrawalAdminDetailDto>> {
        return this.http.post<ApiResponse<WithdrawalAdminDetailDto>>(
            `${this.withdrawalUrl}/${withdrawalId}/fail`,
            payload
        );
    }

    retryWithdrawalRequest(
        withdrawalId: string
    ): Observable<ApiResponse<any>> {
        return this.http.post<ApiResponse<any>>(
            `${this.withdrawalUrl}/${withdrawalId}/retry`,
            {}
        );
    }

    getAdminBeneficiaries(
        userId?: string,
        walletId?: string,
        paymentMethod?: string,
        isActive?: boolean,
        search?: string
    ): Observable<ApiResponse<AdminWithdrawalBeneficiaryDto[]>> {
        let params = new HttpParams();

        if (userId) params = params.set('userId', userId);
        if (walletId) params = params.set('walletId', walletId);
        if (paymentMethod) params = params.set('paymentMethod', paymentMethod);
        if (isActive != null) params = params.set('isActive', isActive);
        if (search) params = params.set('search', search);

        return this.http.get<ApiResponse<AdminWithdrawalBeneficiaryDto[]>>(
            `${environment.apiBaseUrl}/AdminWithdrawalBeneficiaries`,
            { params }
        );
    }

    getAdminBeneficiaryById(
        id: string
    ): Observable<ApiResponse<AdminWithdrawalBeneficiaryDto>> {
        return this.http.get<ApiResponse<AdminWithdrawalBeneficiaryDto>>(
            `${environment.apiBaseUrl}/AdminWithdrawalBeneficiaries/${id}`
        );
    }

    toggleAdminBeneficiaryStatus(
        id: string
    ): Observable<ApiResponse<AdminWithdrawalBeneficiaryDto>> {
        return this.http.put<ApiResponse<AdminWithdrawalBeneficiaryDto>>(
            `${environment.apiBaseUrl}/AdminWithdrawalBeneficiaries/${id}/toggle-status`,
            {}
        );
    }
}